/*
 * Everything the case header reads and edits, composed once so the pill, the
 * popover and the chips share the shell's matter fetch and one set of
 * mutations. Depends on CaseProvider + IndividualProvider + MatterProvider
 * (all wrap the case outlet). The visa is case data, never read from the URL.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { ForeignNationalsAPI } from "../../../api/foreignNationals";
import { IndividualsAPI } from "../../../api/individuals";
import { MattersAPI } from "../../../api/matters";
import type { ForeignNational } from "../../../api/types/foreignNational";
import { CaseStatus } from "../../../api/types/case";
import { useCase } from "../../../contexts/CaseContext";
import { useIndividualOptional } from "../../../contexts/IndividualContext";
import { useUserProfiles } from "../../userProfiles/context/UserProfilesContext";
import { usePracticeTeamMembers } from "../../../hooks/usePracticeTeamMembers";
import { useUserRoleAndSubscription } from "../../../hooks/useUserRoleAndSubscription";
import type { IndividualEmployerInfo } from "../../../redux/individual/individualSlice";
import { VISAVALUE } from "../../../types/visa.type";
import { clientSubtitle } from "../../appTabs/groups";
import { caseTypeLabel } from "../../customCaseTypes/customCaseTypes";
import { companyActions } from "../../companies/actions";
import type { Company } from "../../companies/types";
import { useKeyFacts } from "../../keyFacts/hooks/useKeyFacts";
import { useMatter } from "../context/MatterContext";
import { useCaseRoute } from "../useCaseRoute";
import { useCaseVisa } from "../useCaseVisa";
import {
  displayName,
  immigrationSummary,
  initialsOf,
  workflowLabel,
  type ImmigrationSummary
} from "./caseHeaderModel";

export type TeamMember = {
  uid: string;
  label: string;
  email: string;
  initials: string;
  avatarUrl?: string;
};

export type CaseDetails = {
  isLoading: boolean;
  individualId: string;
  /** The case's visa (case data; EMPTY when unknown). */
  visa: VISAVALUE;
  /** Client (foreign national) display. */
  clientName: string;
  clientInitials: string;
  clientAvatarUrl?: string;
  /** CMS client id when the case is linked to a client record. */
  foreignNationalId: string | null;
  /** Citizenship and birth year, to tell same-name clients apart. */
  clientSubtitle?: string;
  workflow: string;
  status: CaseStatus;
  filingDeadline: string | null;
  petitioner: string;
  assignedUserIds: string[];
  teamMembers: TeamMember[];
  isLoadingTeamMembers: boolean;
  isSavingAssignees: boolean;
  setStatus: (status: CaseStatus) => Promise<void>;
  setFilingDeadline: (iso: string | null) => Promise<void>;
  setPetitioner: (name: string) => Promise<void>;
  /** Link the case to a company record: its details and playbook then apply. */
  linkPetitioner: (company: Company) => Promise<void>;
  /** Create a company with this name and link the case to it. */
  createPetitioner: (name: string) => Promise<void>;
  setAssignees: (userIds: string[]) => Promise<void>;
  /** Change the visa; keeps `Case.visa` and `Individual.visa` in sync. */
  setVisa: (visa: VISAVALUE) => Promise<void>;
  isSavingVisa: boolean;
  /** Receipts, service center, priority date, status expiry, deadlines. */
  immigration: ImmigrationSummary;
  setPremiumProcessing: (on: boolean) => Promise<void>;
};

const str = (v: unknown): string => (typeof v === "string" ? v : "");

export const useCaseDetails = (): CaseDetails => {
  const { individualId: id } = useCaseRoute();
  const {
    case: caseInfo,
    updateCase,
    isLoading: isCaseLoading,
    uscisReceipts
  } = useCase();
  const keyFacts = useKeyFacts(id || null);
  const individualCtx = useIndividualOptional();
  const individual = individualCtx?.individual ?? null;
  const visa = useCaseVisa();
  const {
    matter,
    isLoading: isMatterLoading,
    settled: isMatterSettled,
    foreignNationalId
  } = useMatter();
  const { ensure: ensureProfiles, get: getProfile } = useUserProfiles();
  const { lawyerPractice } = useUserRoleAndSubscription();
  const { teamMembers: rawTeam, isLoading: isLoadingTeamMembers } =
    usePracticeTeamMembers(lawyerPractice);

  const matterId = (caseInfo?.individual_id ?? id).trim();
  const lawyerId = (caseInfo?.lawyer_id ?? "").trim();

  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
  const [foreignNational, setForeignNational] = useState<{
    id: string;
    record: ForeignNational;
  } | null>(null);
  const [isSavingAssignees, setIsSavingAssignees] = useState(false);
  const [isSavingVisa, setIsSavingVisa] = useState(false);

  // Assignees come from the shell's matter; fall back to the case lawyer when
  // the matter could not be read.
  useEffect(() => {
    if (!isMatterSettled) return;
    if (matter) {
      setAssignedUserIds(
        (matter.assigned_to_user_ids ?? [])
          .map((u) => (u ?? "").trim())
          .filter(Boolean)
      );
    } else {
      setAssignedUserIds(lawyerId ? [lawyerId] : []);
    }
  }, [isMatterSettled, lawyerId, matter]);

  // The linked client record, for the name and avatar.
  useEffect(() => {
    let cancelled = false;
    setForeignNational(null);
    if (!foreignNationalId) return undefined;
    void ensureProfiles([foreignNationalId]);
    ForeignNationalsAPI.get(foreignNationalId)
      .then((fn) => {
        if (!cancelled)
          setForeignNational({ id: foreignNationalId, record: fn });
      })
      .catch(() => {
        // Client crumb is best-effort; fall back to the individual's name.
      });
    return () => {
      cancelled = true;
    };
  }, [ensureProfiles, foreignNationalId]);

  // Warm avatars for everyone the header may draw.
  useEffect(() => {
    const ids = [
      individual?.uid,
      lawyerId,
      ...assignedUserIds,
      ...rawTeam.map((m) => m?.uid)
    ]
      .map((v) => (v ?? "").trim())
      .filter(Boolean);
    if (ids.length) void ensureProfiles(ids);
  }, [assignedUserIds, ensureProfiles, individual?.uid, lawyerId, rawTeam]);

  // The fetched record can still be the previous case's client for a render.
  const client =
    foreignNational?.id === foreignNationalId ? foreignNational.record : null;
  const clientName =
    displayName([client?.first_name, client?.middle_name, client?.last_name]) ||
    displayName([
      individual?.firstName,
      individual?.middleName,
      individual?.lastName
    ]);
  const clientInitials = initialsOf(clientName);
  const clientAvatarUrl =
    (foreignNationalId
      ? getProfile(foreignNationalId)?.avatar_data_url
      : null) ??
    (individual?.uid ? getProfile(individual.uid)?.avatar_data_url : null) ??
    undefined;

  const workflow = workflowLabel({
    caseVisa: caseInfo?.visa,
    individualVisa: individual?.visaType,
    workflowType: individual?.workflow_type,
    isRfe: individual?.is_rfe,
    customLabel: caseTypeLabel(caseInfo)
  });

  const employerInfo: IndividualEmployerInfo = individual?.employerInfo ?? {};
  const petitioner = str(employerInfo.legalBusinessName).trim();

  const teamMembers = useMemo<TeamMember[]>(
    () =>
      rawTeam.map((m) => {
        const label =
          displayName([m.firstName, m.middleName, m.lastName]) ||
          (m.email ?? "").trim() ||
          "Unknown";
        return {
          uid: m.uid,
          label,
          email: m.email ?? "",
          initials: initialsOf(label, "CB"),
          avatarUrl: getProfile(m.uid)?.avatar_data_url ?? undefined
        };
      }),
    [getProfile, rawTeam]
  );

  const setStatus = useCallback(
    async (status: CaseStatus) => {
      try {
        await updateCase({ status });
      } catch (e) {
        toast.error("Couldn't update status", {
          description: e instanceof Error ? e.message : undefined
        });
        throw e;
      }
    },
    [updateCase]
  );

  const setFilingDeadline = useCallback(
    async (iso: string | null) => {
      try {
        await updateCase({ filing_deadline: iso ?? "" });
      } catch (e) {
        toast.error("Couldn't update filing deadline", {
          description: e instanceof Error ? e.message : undefined
        });
        throw e;
      }
    },
    [updateCase]
  );

  const setPetitioner = useCallback(
    async (name: string) => {
      if (!caseInfo) return;
      const address = employerInfo.businessAddress;
      try {
        await IndividualsAPI.updateIndividual(caseInfo.individual_id, {
          employerInfo: {
            legalBusinessName: name,
            dba: str(employerInfo.dba),
            contactName: str(employerInfo.contactName),
            contactTitle: str(employerInfo.contactTitle),
            businessAddress: {
              street: str(address?.street),
              city: str(address?.city),
              state: str(address?.state),
              country: str(
                (address as Record<string, unknown> | undefined)?.country
              ),
              zip_code:
                str(address?.zipCode) ||
                str((address as Record<string, unknown> | undefined)?.zip_code)
            }
          },
          employerPetitioner: true
        });
        await individualCtx?.refetchIndividual();
      } catch (e) {
        toast.error("Couldn't update petitioner", {
          description: e instanceof Error ? e.message : undefined
        });
        throw e;
      }
    },
    [caseInfo, employerInfo, individualCtx]
  );

  const linkPetitioner = useCallback(
    async (company: Company) => {
      if (!caseInfo) return;
      const linked = await companyActions.link(company, [
        caseInfo.individual_id
      ]);
      if (!linked) throw new Error("not linked");
      await individualCtx?.refetchIndividual();
    },
    [caseInfo, individualCtx]
  );

  const createPetitioner = useCallback(
    async (name: string) => {
      if (!caseInfo) return;
      const created = await companyActions.create({
        name,
        individual_ids: [caseInfo.individual_id]
      });
      if (!created) throw new Error("not created");
      await individualCtx?.refetchIndividual();
    },
    [caseInfo, individualCtx]
  );

  const setAssignees = useCallback(
    async (next: string[]) => {
      if (!matterId) return;
      const previous = assignedUserIds;
      setAssignedUserIds(next);
      setIsSavingAssignees(true);
      try {
        await MattersAPI.update(matterId, {
          individual: { assigned_to_user_ids: next }
        });
        toast.success("Assignment updated");
      } catch (e) {
        setAssignedUserIds(previous);
        toast.error("Couldn't update assignment", {
          description: e instanceof Error ? e.message : undefined
        });
      } finally {
        setIsSavingAssignees(false);
      }
    },
    [assignedUserIds, matterId]
  );

  const setVisa = useCallback(
    async (next: VISAVALUE) => {
      if (!caseInfo || next === VISAVALUE.EMPTY) return;
      setIsSavingVisa(true);
      try {
        // Matters filter on the case; agents and legacy screens read the
        // individual, so both records change together.
        await updateCase({ visa: next });
        await IndividualsAPI.updateIndividual(caseInfo.individual_id, {
          visa: next
        });
        await individualCtx?.refetchIndividual();
        toast.success("Workflow updated");
      } catch (e) {
        toast.error("Couldn't update workflow", {
          description: e instanceof Error ? e.message : undefined
        });
        throw e;
      } finally {
        setIsSavingVisa(false);
      }
    },
    [caseInfo, individualCtx, updateCase]
  );

  const facts = keyFacts.data?.facts;
  const immigration = useMemo(
    () =>
      immigrationSummary({
        facts: facts ?? [],
        trackedReceipts: uscisReceipts
          .filter((r) => !r.is_deleted)
          .map((r) => r.uscis_receipt_number),
        client,
        caseInfo
      }),
    [caseInfo, client, facts, uscisReceipts]
  );

  const setPremiumProcessing = useCallback(
    async (on: boolean) => {
      try {
        await updateCase({ premium_processing: on });
      } catch (e) {
        toast.error("Couldn't update premium processing", {
          description: e instanceof Error ? e.message : undefined
        });
        throw e;
      }
    },
    [updateCase]
  );

  return {
    isLoading:
      isCaseLoading || Boolean(individualCtx?.isLoading) || isMatterLoading,
    individualId: id,
    visa,
    clientName,
    clientInitials,
    clientAvatarUrl,
    foreignNationalId,
    clientSubtitle: clientSubtitle(client),
    workflow,
    status: caseInfo?.status ?? CaseStatus.IN_PROGRESS,
    filingDeadline: caseInfo?.filing_deadline ?? null,
    petitioner,
    assignedUserIds,
    teamMembers,
    isLoadingTeamMembers,
    isSavingAssignees,
    setStatus,
    setFilingDeadline,
    setPetitioner,
    linkPetitioner,
    createPetitioner,
    setAssignees,
    setVisa,
    isSavingVisa,
    immigration,
    setPremiumProcessing
  };
};
